/**
 * Pure data driving the Sentry scrubbers (ADR-037, tech-spec §12.3, S36).
 *
 * The three patterns redact markdown-shaped substrings that may appear
 * inside `error.message` or `exception.value`. They are deliberately
 * over-eager: we'd rather lose some debugging fidelity than ship a
 * content leak (Mission constraints #1 and #5 in
 * `docs/vision/mission.md`).
 *
 * The breadcrumb deny-list drops categories whose payloads commonly
 * carry DOM text or full URLs. Call sites that need to keep a breadcrumb
 * from a deny-listed category MUST opt in by setting
 * `breadcrumb.data.contentSafe = true` and earn that flag in code review.
 */

export const REDACTED_PREFIX = '<scrubbed:length='
export const REDACTED_SUFFIX = '>'

export function redactionMarker(length: number): string {
  return `${REDACTED_PREFIX}${length}${REDACTED_SUFFIX}`
}

export const MARKDOWN_SHAPED_PATTERNS: readonly RegExp[] = [
  // Quoted heading like `'## Title'` or `"### Section"` — any 1-6 hashes followed by whitespace.
  /(['"])(#{1,6}\s+[^'"\n]{0,200})\1/g,
  // Triple-backtick fenced code block (cap at 4 KB to avoid catastrophic backtracking on malformed input).
  /```[\s\S]{0,4096}?```/g,
  // Quoted prose: an opening quote, up to 200 non-quote chars, a whitespace, then 12+ non-quote
  // chars, then a matching close. Catches body snippets like 'The project codename is Atlas',
  // skips short identifiers like 'foo-123' and nested quotes like '...reading "x"'.
  /(['"])([^'"\n]{0,200}\s[^'"\n]{12,500})\1/g,
]

export const BREADCRUMB_CATEGORY_DENY_LIST: ReadonlySet<string> = new Set([
  'ui.click',
  'ui.input',
  'console',
  'navigation',
])

export const CONTENT_SAFE_BREADCRUMB_FLAG = 'contentSafe' as const